import { useTranslation } from 'react-i18next';

import { Typography } from '@cfa/react-components';

import { formatDate } from '@/utils/date';
import { cn } from '@/utils/tailwind';

interface ProgressBarProps {
  current: number;
  isComplete: boolean;
  isDisabled?: boolean;
  isReports?: boolean;
  startDate?: string;
  text: string;
  total: number;
}

const getProgressBarWidth = (current: number, total: number) => {
  if (current === 0 && total === 0) {
    return 0;
  }
  const percent = ((current / total) * 100).toFixed(2);
  return Number(percent) > 100 ? 100 : percent;
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  isComplete,
  isDisabled,
  isReports,
  startDate,
  text,
  total,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex w-full justify-between">
        {isReports ? (
          <div className="text-left">
            <Typography
              className={cn(isDisabled && 'text-grayscale-gray3')}
              variant="body2"
              data-testid="ProgressBarText"
            >
              {text}
            </Typography>
            {!!startDate && (
              <Typography
                className={cn(isDisabled && 'text-grayscale-gray3')}
                variant="body2"
              >
                {`${t('Reports.filtering.startDate')}: ${formatDate(startDate)}`}
              </Typography>
            )}
          </div>
        ) : (
          <p
            className={cn(
              isComplete && 'text-brand-green! font-bold',
              isDisabled && 'text-grayscale-gray3',
              'text-variant-body2',
            )}
            data-testid="ProgressBarText"
          >
            {text}
          </p>
        )}
      </div>

      {!isComplete && !!total && (
        <div className="bg-grayscale-gray2 mt-1.5 mb-0.5 h-1 w-full rounded-lg">
          <div
            className={cn(
              'h-1 rounded-lg',
              isDisabled ? 'bg-grayscale-gray3' : 'bg-brand-green',
            )}
            style={{
              width:
                current && total
                  ? `${getProgressBarWidth(current, total)}%`
                  : '0',
            }}
          />
        </div>
      )}
    </>
  );
};

export default ProgressBar;
